var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo } from "react";
import isEqual from "lodash.isequal";
import { TableCell as MuiTableCell, } from "@mui/material";
// components
import { Tags, TextCell, IconCell, CollapseIconCell, CheckboxCell, BadgeCell, MultiTextTooltipCell, ImageCell, PopoverMenuCell, } from "../../Atoms";
import UserCell from "../../Atoms/UserCell";
import { Tag as TagCell } from "../../../../Atoms";
var defaultThProps = {
    variant: "body3",
    emphasis: "medium",
};
var defaultTdProps = {
    variant: "body2",
    emphasis: "high",
};
var TableCell = function (_a) {
    var _b;
    var content = _a.content, clickHandler = _a.clickHandler, children = _a.children, _c = _a.cellData, cellData = _c === void 0 ? {} : _c, columnName = _a.columnName, footer = _a.footer, collapseOpen = _a.collapseOpen, _d = _a.data, data = _d === void 0 ? {} : _d, 
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    memoComparitor = _a.memoComparitor, // needs to be here so it's not part of "...rest"
    head = _a.head, cellId = _a.cellId, hasTags = _a.hasTags, _e = _a.isFetching, isFetching = _e === void 0 ? false : _e, _f = _a.hover, hover = _f === void 0 ? false : _f, rest = __rest(_a, ["content", "clickHandler", "children", "cellData", "columnName", "footer", "collapseOpen", "data", "memoComparitor", "head", "cellId", "hasTags", "isFetching", "hover"]);
    var Icon = cellData.Icon, _g = cellData.iconSize, iconSize = _g === void 0 ? "18px" : _g, onChange = cellData.onChange, CheckedIcon = cellData.CheckedIcon, color = cellData.color, width = cellData.width, _h = cellData.tdBodyProps, tdBodyProps = _h === void 0 ? {} : _h, _j = cellData.tdHeadProps, tdHeadProps = _j === void 0 ? {} : _j, _k = cellData.valign, valign = _k === void 0 ? "middle" : _k, _l = cellData.align, align = _l === void 0 ? "left" : _l, _m = cellData.tooltip, tooltip = _m === void 0 ? "" : _m, toolTipIcon = cellData.toolTipIcon, userCellData = cellData.userCellData, multiTextTooltipData = cellData.multiTextTooltipData, asHTML = cellData.asHTML, badgeCellData = cellData.badgeCellData, _o = cellData.secondaryTooltip, secondaryTooltip = _o === void 0 ? "" : _o, sort = cellData.sort, imageCellData = cellData.imageCellData, tagCellData = cellData.tagCellData, popoverMenuCellData = cellData.popoverMenuCellData, tagsData = cellData.tagsData, buttons = cellData.buttons;
    var textProps = tdBodyProps.textProps;
    var thTextProps = tdHeadProps.textProps;
    var mergedTextProps = head
        ? __assign(__assign({}, defaultThProps), thTextProps) : __assign(__assign({}, defaultTdProps), textProps);
    /**
     * handle side effects then call click handler
     * If side effects exist dont run click event unless input is not clicked
     */
    var handleClick = function (e) {
        /* istanbul ignore else  */
        if (clickHandler &&
            data &&
            (!onChange || !(e.target instanceof HTMLInputElement))) {
            clickHandler(data);
        }
    };
    /**
     * handle change if cell contains an input
     */
    var handleChange = function () {
        /* istanbul ignore else  */
        if (onChange && data) {
            onChange(data);
        }
    };
    /**
     * renders various types of content based on cell style
     */
    var renderContent = function () {
        var id = data.id;
        if (isFetching) {
            return (_jsx(TextCell, { columnName: columnName, id: id, head: head, tooltip: tooltip, textProps: mergedTextProps, content: undefined }));
        }
        switch (cellData === null || cellData === void 0 ? void 0 : cellData.style) {
            case "text":
                return (_jsx(TextCell, { columnName: columnName, id: id, head: head, tooltip: tooltip, toolTipIcon: toolTipIcon, textProps: mergedTextProps, content: content, clickHandler: clickHandler, sort: sort, asHTML: asHTML, buttons: buttons, data: data }));
            case "multiTextTooltip":
                if (multiTextTooltipData) {
                    return (_jsx(MultiTextTooltipCell, __assign({}, multiTextTooltipData, { textProps: mergedTextProps })));
                }
                return null;
            case "icon":
                return (_jsx(IconCell, { Icon: Icon, tooltip: tooltip, id: id, columnName: columnName, content: content, iconSize: iconSize, align: align, color: color }));
            case "collapse":
                return _jsx(CollapseIconCell, { collapseOpen: collapseOpen });
            case "checkbox":
                return (_jsx(CheckboxCell, { columnName: columnName, id: id, checked: cellData.checked, tooltip: tooltip, secondaryTooltip: secondaryTooltip, handleChange: handleChange, CheckedIcon: CheckedIcon, Icon: Icon }));
            case "user":
                if (userCellData) {
                    return _jsx(UserCell, __assign({}, userCellData, { asHTML: asHTML }));
                }
                return null;
            case "badge":
                if (badgeCellData) {
                    return _jsx(BadgeCell, __assign({}, badgeCellData));
                }
                return null;
            case "image":
                // content is the url of the image so we want to make sure
                // the value of content is a string
                if (content && typeof content === "string") {
                    return (_jsx(ImageCell, __assign({ id: id, columnName: columnName, content: content }, imageCellData)));
                }
                return null;
            case "tag":
                if (tagCellData) {
                    return _jsx(TagCell, __assign({}, tagCellData));
                }
                return null;
            case "popoverMenu": {
                return (_jsx(PopoverMenuCell, __assign({}, popoverMenuCellData, { open: hover, data: data })));
            }
            default:
                return children;
        }
    };
    var sxStyles = useMemo(function () {
        return (__assign(__assign(__assign({ border: "none", cursor: clickHandler ? "pointer" : "default", verticalAlign: valign, py: head || footer ? 0 : 5, px: 6 }, (columnName === "starred" || columnName === "collapse"
            ? { px: 0 }
            : {})), (hasTags ? { pb: 0 } : {})), { ":first-of-type": {
                pl: 8,
            }, ":last-child": {
                pr: 8,
            }, maxWidth: 545, "&.MuiTableCell-checkbox": {
                pl: 1,
                pr: 0,
                "+.MuiTableCell-root": {
                    pl: 3,
                },
            } }));
    }, [valign, head, footer, columnName, hasTags]);
    var styles = useMemo(function () { return (__assign({}, (width ? { style: { width: width } } : {}))); }, [width]);
    return (_jsxs(MuiTableCell, __assign({ onClick: handleClick }, rest, { "data-testid": "".concat(columnName, "-").concat(cellId), align: align, className: "MuiTableCell-".concat(columnName), sx: sxStyles }, styles, { children: [renderContent(), ((_b = tagsData === null || tagsData === void 0 ? void 0 : tagsData.tags) === null || _b === void 0 ? void 0 : _b.length) ? _jsx(Tags, __assign({}, tagsData)) : null] })));
};
/* istanbul ignore next */
export var MemoTableCell = React.memo(TableCell, function (prev, next) {
    var _a;
    if (!isEqual(prev.cellData, next.cellData) ||
        (prev.head && prev.columnName === "checkbox") ||
        ((_a = prev.cellData) === null || _a === void 0 ? void 0 : _a.style) === "popoverMenu" ||
        prev.content !== next.content ||
        prev.memoComparitor !== next.memoComparitor) {
        return false;
    }
    return true;
});
export default TableCell;
